import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
	ViewModifierGroupContext,
	IViewModifierGroupContext,
} from '../../viewModifierGroupState/viewModifierGroupContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RestaurantModGroupPropertiesType, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { ViewPageBlock } from 'components/ViewPage/ViewPageBlock';
import { ViewPageSection } from 'components/ViewPage/ViewPageSection';
import { ItemList } from 'components/ViewPage//ItemList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import Button from 'components/Button/Button';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { Badge } from 'components/Badge';
import { PortionFlow } from './components/PortionFlow';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './index.module.scss';

export const ViewForm: React.FC = () => {
	const {
		setTranslationTab,
		changeTypeOfModalWindow,
		setRestaurantIdForRestaurantProps,
		modifierGroupFullInfo,
		accessibleRestaurants,
		selectedTranslationTab,
		restaurantIdForRestaurantProps,
		restaurantProps,
	} = useContext<IViewModifierGroupContext>(ViewModifierGroupContext);
	const { t } = useTranslation('form');
	const listOfRestaurants = useMemo(() => accessibleRestaurants, [accessibleRestaurants]);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const mappedRestaurantOptions = useMemo<ISelectOption[]>(() => {
		const visibleItems = showArchived
			? accessibleRestaurants
			: accessibleRestaurants.filter(
					(restaurant) =>
						restaurant.status === EntityStatus.Active || restaurant.id === restaurantIdForRestaurantProps,
			  );

		return visibleItems.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [accessibleRestaurants, showArchived, restaurantIdForRestaurantProps]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	const forceDisable = restaurantProps?.forceDisable;
	const forceDisableValue = forceDisable ? t('yes') : t('no');

	const parentElements = useMemo(() => restaurantProps?.parents || [], [restaurantProps?.parents]);
	const childModifiers = useMemo(() => restaurantProps?.children || [], [restaurantProps?.children]);

	const getElementLink = useCallback((id: string, type: RestaurantModGroupPropertiesType): Nullable<string> => {
		if (
			type === RestaurantModGroupPropertiesType.Dish ||
			type === RestaurantModGroupPropertiesType.DishAndModifier
		) {
			return `/menu-v2/dishes/view?dishID=${id}`;
		}
		if (type === RestaurantModGroupPropertiesType.Modifier) {
			return `/menu-v2/modifiers/view?modifierID=${id}`;
		}
		return null;
	}, []);

	const handleClickAssignRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignRestaurants'),
		[changeTypeOfModalWindow],
	);

	const handleClickEditInclusive = useCallback(() => {
		changeTypeOfModalWindow('editInclusive');
	}, [changeTypeOfModalWindow]);

	const handleClickEditModifiersTemplate = useCallback(
		() => changeTypeOfModalWindow('editModifiersTemplate'),
		[changeTypeOfModalWindow],
	);

	return (
		<div className={style.container} data-test="ViewForm">
			<div className={style.content}>
				<ViewPageBlock heading="generalPropertiesHeading">
					<ViewPageSection heading="mainInfoHeading">
						<FullInfoField label="nameOfModifierGroupLabel" value={modifierGroupFullInfo?.name || '-'} />
						<FullInfoField label="customerLabel" value={modifierGroupFullInfo?.customer.name || '-'} />
						<FullInfoField label="articleNumberLabel" value={modifierGroupFullInfo?.articleNumber || '-'} />
						<FullInfoField label="externalIdLabel" value={modifierGroupFullInfo?.externalId || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="translationsHeading">
						<MenuTranslationForm
							className={style.translationForm}
							setTranslationTab={setTranslationTab}
							isShowLanguageName={false}
							IDSelectedCustomer={modifierGroupFullInfo?.customer.id || ''}
							translations={modifierGroupFullInfo?.translations || []}
							selectedTranslationTab={selectedTranslationTab}
							nameLabel="nameOfModifierGroupLabel"
						/>
					</ViewPageSection>

					<ViewPageSection heading="restaurantsHeading">
						{listOfRestaurants.length ? (
							<ItemList
								itemListLabel="restaurantListHeading"
								items={listOfRestaurants}
								pathToItem="/clients/restaurants?restaurantFullInfoID="
								assignBtnTranslationKey="editRestaurantsList"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						) : (
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerModifierGroupToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						)}
					</ViewPageSection>

					<div className={style.btnWrapper} data-test="BtnBlock1">
						{/* <Button theme="primaryBlue" disabled={true} onClick={() => console.log('Check tree view')}>
							<Text translationNameSpace="form" translationKey="checkTreeViewButton" asFragment={true} />
						</Button> */}
						<Link
							target={`blank_${Date.now()}`}
							rel="noopener noreferrer"
							to={`/menu-v2/modifier-groups/edit?modifierGroupID=${modifierGroupFullInfo?.id}`}>
							<Button
								className={style.btn}
								theme="primaryBlue"
								icon={
									<svg className={style.icon}>
										<use xlinkHref={`${breadcrumbs}#pencil`} />
									</svg>
								}>
								<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
							</Button>
						</Link>
					</div>
				</ViewPageBlock>

				<ViewPageBlock heading="restaurantPropertiesHeading">
					{accessibleRestaurants.length ? (
						<>
							<OutsideFormSelect
								testId="restaurantProperties"
								label="restaurantsLabel"
								name="restaurantProperties"
								value={restaurantIdForRestaurantProps}
								options={mappedRestaurantOptions}
								onChange={setRestaurantIdForRestaurantProps}
								keepSearchWithDropdownRender={true}
								dropdownRender={renderSelectMenu}
							/>

							<ViewPageSection heading="isForceDisabledLabel">
								{restaurantProps ? (
									<FullInfoField
										value={forceDisableValue}
										prefixIcon="asCheckbox"
										prefixIconName={forceDisable ? 'checkMark' : 'crossMark'}
										prefixIconIsChecked={forceDisable}
										containerClassName={style.noMargin}
										inputWrapperClassName={style.noBorder}
									/>
								) : (
									<FullInfoField
										value="-"
										containerClassName={style.noMargin}
										inputWrapperClassName={style.noBorder}
									/>
								)}
							</ViewPageSection>

							<ViewPageSection heading="elementsIncludeThisGroupHeading">
								{restaurantIdForRestaurantProps && parentElements.length > 0 && (
									<div className={style.elementsList}>
										{parentElements.map((parent) => {
											const parentLink = getElementLink(parent.id, parent.type);
											return (
												<div key={parent.id} className={style.elementItem}>
													<div className={style.dot} />
													{parentLink ? (
														<Link
															className={style.link}
															target={`blank_${Date.now()}`}
															rel="noopener noreferrer"
															to={parentLink}>
															<Text
																className={style.name}
																translationKey={parent.name}
																size="s"
																withTooltip={true}
															/>
														</Link>
													) : (
														<Text
															className={style.name}
															translationKey={parent.name}
															size="s"
															withTooltip={true}
														/>
													)}
													{parent.inclusive > 0 && (
														<Badge color="green" size="sm">
															<Text
																translationNameSpace="form"
																translationKey="inclusiveLabel"
																size="xs"
															/>
														</Badge>
													)}
												</div>
											);
										})}
									</div>
								)}
								{restaurantIdForRestaurantProps && parentElements.length === 0 && (
									<Text
										translationNameSpace="form"
										translationKey="noElementsIncludeThisGroup"
										size="s"
									/>
								)}
								{!restaurantIdForRestaurantProps && (
									<Text translationNameSpace="form" translationKey="-" />
								)}
								{!restaurantProps?.isPortionFlow && (
									<Button
										className={style.editBtn}
										size="sizeS"
										theme="outlined"
										onClick={handleClickEditInclusive}
										disabled={!restaurantIdForRestaurantProps || parentElements.length === 0}
										icon={
											<svg className={style.icon}>
												<use xlinkHref={`${breadcrumbs}#pencil`} />
											</svg>
										}>
										<Text
											translationNameSpace="form"
											translationKey="editInclusiveButton"
											asFragment={true}
										/>
									</Button>
								)}
							</ViewPageSection>

							{/* <ViewPageSection heading="modifiersOfThisGroupHeading"> */}
							<ViewPageSection
								heading={
									restaurantProps?.isPortionFlow ? 'modifiersHeading' : 'modifiersOfThisGroupHeading'
								}>
								{restaurantProps?.isPortionFlow &&
									restaurantIdForRestaurantProps &&
									childModifiers.length > 0 && (
										<div className={style.isPortionFlow}>
											<Text
												translationNameSpace="form"
												translationKey="modGroupPortionDescription"
												theme="neutralLight"
												size="s"
											/>
											<Text
												translationNameSpace="form"
												translationKey="modGroupPortionDescription2"
												size="s"
											/>
											<PortionFlow
												parentChildRelations={restaurantProps?.parentChildRelations || []}
											/>
										</div>
									)}
								{!restaurantProps?.isPortionFlow &&
									restaurantIdForRestaurantProps &&
									childModifiers.length > 0 && (
										<div className={style.elementsList}>
											{childModifiers.map((modifier) => {
												const childLink = getElementLink(modifier.id, modifier.type);
												return (
													<div key={modifier.id} className={style.elementItem}>
														<div className={style.dot} />
														{childLink ? (
															<Link
																className={style.link}
																target={`blank_${Date.now()}`}
																rel="noopener noreferrer"
																to={childLink}>
																<Text
																	className={style.name}
																	translationKey={modifier.name}
																	size="s"
																	withTooltip={true}
																/>
															</Link>
														) : (
															<Text
																className={style.name}
																translationKey={modifier.name}
																size="s"
																withTooltip={true}
															/>
														)}
														{modifier.isDefault && (
															<Badge color="green" size="sm">
																<Text
																	translationNameSpace="form"
																	translationKey="isDefaultLabel"
																	size="xs"
																/>
															</Badge>
														)}
													</div>
												);
											})}
										</div>
									)}
								{restaurantIdForRestaurantProps && childModifiers.length === 0 && (
									<Text
										translationNameSpace="form"
										translationKey="noModifiersInThisGroup"
										size="s"
									/>
								)}
								{!restaurantIdForRestaurantProps && (
									<Text translationNameSpace="form" translationKey="-" />
								)}
								{!restaurantProps?.isPortionFlow && (
									<Button
										className={style.editBtn}
										size="sizeS"
										theme="outlined"
										onClick={handleClickEditModifiersTemplate}
										icon={
											<svg className={style.icon}>
												<use xlinkHref={`${breadcrumbs}#pencil`} />
											</svg>
										}>
										<Text
											translationNameSpace="form"
											translationKey="modifiersTemplateButton"
											asFragment={true}
										/>
									</Button>
								)}
							</ViewPageSection>
						</>
					) : (
						<ViewPageSection heading="restaurantsHeading">
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerModifierGroupToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						</ViewPageSection>
					)}

					{restaurantIdForRestaurantProps && (
						<div className={style.btnWrapper} data-test="BtnBlock2">
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/modifier-groups/edit-restaurant-properties?modifierGroupID=${
									modifierGroupFullInfo?.id || ''
								}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						</div>
					)}
				</ViewPageBlock>
			</div>
		</div>
	);
};
